import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

// Size scale (rev 2 — bumped up ~1 step across the board):
//   xs       h-6   was `sm`
//   sm       h-8   was `lg`
//   default  h-9   new — larger than the previous default (h-7)
//   lg       h-11  new
// Icon-only variants track the same heights: icon-xs / icon-sm / icon / icon-lg.
const buttonVariants = cva(
  // font-semibold, not font-medium — the Vercel Fleet M2 prototype's buttons
  // (rcBtn, askTab, filter drawer Apply/Reset, etc.) are consistently
  // fontWeight:600 across every variant, not just the filled/primary one.
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-semibold transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-md",
  {
    variants: {
      variant: {
        default: "bg-brand-indigo text-white hover:bg-brand-indigo/90",
        destructive: "bg-destructive text-white hover:bg-destructive/90",
        outline: "border border-border text-foreground hover:bg-neutral-100",
        // Outline, red — a reject/decline affordance that reads as
        // destructive at a glance without being a solid fill (the solid
        // `destructive` variant is for the actual confirm step). Uses the
        // HITL success/danger tokens (--hitl-danger), the same pair the For
        // Review Proceed/Reject buttons use, not the generic --destructive.
        "outline-destructive":
          "border border-hitl-danger text-hitl-danger bg-background hover:bg-hitl-danger-50",
        secondary: "bg-neutral-100 text-foreground hover:bg-neutral-200",
        ghost:
          "text-muted-foreground hover:text-foreground hover:bg-neutral-100",
        link: "text-foreground underline-offset-4 hover:underline",
      },
      size: {
        xs: "h-6 px-2 text-xs [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 px-3 text-sm [&_svg:not([class*='size-'])]:size-3.5",
        default: "h-9 px-3.5 text-base [&_svg:not([class*='size-'])]:size-4",
        lg: "h-11 px-5 text-md [&_svg:not([class*='size-'])]:size-4",
        icon: "size-9 [&_svg:not([class*='size-'])]:size-4",
        "icon-xs": "size-6 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8 [&_svg:not([class*='size-'])]:size-3.5",
        "icon-lg": "size-11 [&_svg:not([class*='size-'])]:size-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

interface ButtonProps
  extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Icon rendered before the label. Any React node; typical use is a
   * FontAwesomeIcon or lucide icon. The button's built-in `gap-1.5` and
   * size-scoped SVG sizing handle spacing + dimensions.
   *
   * When `asChild` is true, the icon is injected into the slotted child's
   * existing children (before its own children) so patterns like
   * `<Button asChild leadingIcon={...}><Link>label</Link></Button>` work.
   */
  leadingIcon?: React.ReactNode;
  /**
   * Icon rendered after the label. Same rules as `leadingIcon`.
   */
  trailingIcon?: React.ReactNode;
}

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  leadingIcon,
  trailingIcon,
  children,
  ...props
}: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size, className }));

  if (asChild) {
    // Slot forwards props into a single child element. To make leading /
    // trailing icons work with `asChild` (e.g. `<Button asChild><Link/></Button>`),
    // clone the child so its `children` sit between the icons.
    const child = React.Children.only(children) as React.ReactElement<{
      children?: React.ReactNode;
    }>;
    const composedChildren =
      leadingIcon || trailingIcon ? (
        <>
          {leadingIcon}
          {child.props.children}
          {trailingIcon}
        </>
      ) : (
        child.props.children
      );

    return (
      <Slot
        data-slot="button"
        data-variant={variant}
        data-size={size}
        className={classes}
        {...props}
      >
        {React.cloneElement(child, undefined, composedChildren)}
      </Slot>
    );
  }

  return (
    <button
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={classes}
      {...props}
    >
      {leadingIcon}
      {children}
      {trailingIcon}
    </button>
  );
}

export { Button, buttonVariants };
